import { Plugin } from '@nuxt/types'
import axios from 'axios'

interface RevisionData {
  revisionId: string
}

/**
 * リビジョンチェックプラグイン
 *
 * 30秒ごとに/revision.jsonをチェックし、バージョンが変わったら自動リロード
 * - production/staging環境のみ有効
 * - フォーム入力中はリロードを避ける
 * - 非アクティブタブではポーリング停止（コスト削減）
 * - エラーは無視
 */
const revisionCheckPlugin: Plugin = () => {
  // クライアントサイドのみ実行
  if (process.client) {
    const NODE_ENV = process.env.NODE_ENV

    // production/staging環境のみ有効
    if (NODE_ENV !== 'production' && NODE_ENV !== 'staging') {
      return
    }

    const CHECK_INTERVAL = 30000 // 30秒
    let currentRevisionId: string | null = null
    let checkTimer: number | null = null
    let isChecking = false

    /**
     * ユーザーがフォーム入力中かどうかを判定
     */
    const isUserInputting = (): boolean => {
      const activeElement = document.activeElement
      if (!activeElement) {
        return false
      }

      const tagName = activeElement.tagName.toLowerCase()
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
        return true
      }

      if ((activeElement as HTMLElement).isContentEditable) {
        return true
      }

      return false
    }

    /**
     * revision.jsonをfetchしてチェック
     */
    const checkRevision = async (): Promise<void> => {
      if (isChecking) return
      isChecking = true
      try {
        // キャッシュを避けるためにタイムスタンプを付与
        const timestamp = new Date().getTime()
        const response = await axios.get<RevisionData>(`/revision.json?t=${timestamp}`, {
          timeout: 5000 // 5秒でタイムアウト
        })

        if (!response.data || !response.data.revisionId) {
          return
        }

        const revisionData = response.data

        // 初回取得時は現在のバージョンとして保存
        if (currentRevisionId === null) {
          currentRevisionId = revisionData.revisionId
          return
        }

        // バージョンが変わったかチェック
        if (revisionData.revisionId !== currentRevisionId) {
          // フォーム入力中は次回チェックまで待機
          if (isUserInputting()) {
            return
          }

          // バージョン変更時にリロード
          window.location.reload()
        }
      } catch (error) {
        // エラーは無視（ネットワークエラーなど）
        console.warn('[RevisionCheck] Failed to check revision:', error)
      } finally {
        isChecking = false
      }
    }

    /**
     * 定期チェックを開始
     */
    const startRevisionCheck = (): void => {
      // 初回チェック
      checkRevision()

      checkTimer = window.setInterval(() => {
        checkRevision()
      }, CHECK_INTERVAL)
    }

    /**
     * 定期チェックを停止
     */
    const stopRevisionCheck = (): void => {
      if (checkTimer !== null) {
        clearInterval(checkTimer)
        checkTimer = null
      }
    }

    // DOMContentLoaded後に開始
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startRevisionCheck)
    } else {
      startRevisionCheck()
    }

    // Page Visibility API: 非アクティブタブでポーリング停止
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopRevisionCheck()
      } else if (checkTimer === null) {
        // タブがアクティブになったら再開
        startRevisionCheck()
      }
    })

    // クリーンアップ（通常は不要だが念のため）
    window.addEventListener('beforeunload', () => {
      stopRevisionCheck()
    })
  }
}

export default revisionCheckPlugin
